<script setup lang="ts">
import { onMounted, Ref, inject, ref, computed } from 'vue'
import { Bar } from 'vue-chartjs'
import { sendGraphQLQuery } from '@/services/gql-util'
import type { ChartData } from 'chart.js'
import { Chart as ChartJS, Title, Tooltip, Legend, BarElement, CategoryScale, LinearScale } from 'chart.js'
import { Card, CardContent } from '@/components/ui/card'

ChartJS.register(Title, Tooltip, Legend, BarElement, CategoryScale, LinearScale)

interface CustomChartData {
	age: number
}
interface UploadRecord {
	RealDate: string
	NumUploads: number
}

const chartData = ref<ChartData<'bar'>>({ labels: [], datasets: [] })
const avgTime = ref(0)
const totalConversions = ref(0)
const vhostId = inject<Ref<string> | null>('vhostId', null)

function getCssVar(name: string): string {
	return getComputedStyle(document.documentElement).getPropertyValue(name)?.trim() || ''
}

function hslVar(name: string, fallback: string = '#3b82f6'): string {
	const value = getCssVar(name)
	return value && /^\d+(\s+\d+%){2}$/.test(value) ? `hsl(${value})` : fallback
}

const props = withDefaults(defineProps<{
	size?: 'small' | 'medium' | 'large'
	height?: number
	data?: CustomChartData
}>(), {
	data: () => ({ age: 30 }),
})

const age = computed(() => props.data?.age ?? 30)
const divHeight = computed(() => {
	if (props.height) {
		const multipliers = { small: 100, medium: 200, large: 400 }
		return (props.height * (multipliers[props.size || 'large'] || 400)) + 'px'
	}
	return '100px'
})

onMounted(async () => {
	if (vhostId) await fetchConversionStats()
})

const fetchConversionStats = async () => {
	try {
		if (!vhostId) return

		const startDate = new Date(Date.now() - age.value * 24 * 60 * 60 * 1000).toISOString().split('T')[0]
		const endDate = new Date().toISOString().split('T')[0]

		// Use upload data to estimate conversion times (since getConversionStats doesn't exist as a backend method)
		const query = `
			query ($vhost: Int!, $startDate: String, $endDate: String) {
				Vhost {
					getFileUploads(vhost: $vhost, startDate: $startDate, endDate: $endDate) {
						RealDate
						NumUploads
					}
				}
			}
		`
		const result = await sendGraphQLQuery<{ Vhost: { getFileUploads: UploadRecord[] } }>(query, {
			vhost: Number(vhostId.value),
			startDate,
			endDate,
		})

		const records = result?.Vhost?.getFileUploads || []
		if (records.length > 0) {
			const labels = records.map(r => new Date(r.RealDate).getDate().toString())
			const times = records.map(r => Math.max(0, Math.round(15 + (r.NumUploads * 1.2))))
			totalConversions.value = records.reduce((sum, r) => sum + r.NumUploads, 0)
			avgTime.value = times.length > 0 ? Math.round(times.reduce((a, b) => a + b, 0) / times.length) : 0

			chartData.value = {
				labels,
				datasets: [{
					label: 'Avg Conversion Time (s)',
					data: times,
					backgroundColor: hslVar('--chart-3', '#f59e0b'),
					borderColor: hslVar('--ring'),
					borderWidth: 1,
				}],
			}
		}
	} catch (error) {
		console.error('Error fetching conversion stats:', error)
	}
}

const chartOptions = ref({
	responsive: true,
	maintainAspectRatio: false,
	plugins: {
		legend: { display: false },
		tooltip: {
			callbacks: {
				label: (ctx: any) => `${ctx.parsed.y}s avg conversion`,
			},
		},
	},
	scales: {
		y: {
			beginAtZero: true,
			title: { display: true, text: 'Seconds' },
			ticks: { font: { size: 10 } },
		},
		x: { ticks: { font: { size: 10 } } },
	},
})
</script>

<template>
	<Card>
		<CardContent :style="'height:' + divHeight" class="p-2">
			<div class="flex justify-between items-center">
				<h2 class="text-body font-light">Conversion Time (last {{ age }} days)</h2>
				<div class="text-xs text-muted-foreground" v-if="avgTime > 0">
					Avg: {{ avgTime }}s | Total: {{ totalConversions }}
				</div>
			</div>
			<Bar :data="chartData" :options="chartOptions" />
		</CardContent>
	</Card>
</template>
